/**
 * API 客户端封装（基于原生 fetch）
 *
 * 用于对接 Java 后端（Spring Boot）REST 接口。
 *
 * 约定：
 *  - Java 后端统一返回结构：{ code, message, data }
 *  - 分页接口返回结构：      { code, message, data: { records, total, ... } }
 *  - 认证方式：Bearer Token（accessToken，Authorization: Bearer 头）
 *  - accessToken 过期时（HTTP 401）自动用 refreshToken 刷新
 *
 * 用法：
 *   import { apiClient } from '@/lib/api/client'
 *   const data = await apiClient.get('/listings', { page: 1 })
 */
var _a;
/** 业务异常 */
export class ApiError extends Error {
    constructor(code, message) {
        super(message);
        this.name = 'ApiError';
        this.code = code;
    }
}
const DEFAULT_TIMEOUT = 15000;
/** 后端基础地址：优先读环境变量，兜底本地 Java 服务 */
export const API_BASE_URL = (_a = process.env.NEXT_PUBLIC_API_BASE_URL) !== null && _a !== void 0 ? _a : 'http://localhost:8080/api';
/* ========== 本地缓存的 key ========== */
const STORAGE_KEYS = {
    accessToken: 'guineemall_access_token',
    refreshToken: 'guineemall_refresh_token',
    expiresAt: 'guineemall_expires_at', // accessToken 过期时间戳（ms）
    userInfo: 'guineemall_user_info',
};
/* ========== Token 存取 ========== */
export function getToken() {
    if (typeof window === 'undefined')
        return null;
    return localStorage.getItem(STORAGE_KEYS.accessToken);
}
export function getRefreshToken() {
    if (typeof window === 'undefined')
        return null;
    return localStorage.getItem(STORAGE_KEYS.refreshToken);
}
export function getUserInfo() {
    if (typeof window === 'undefined')
        return null;
    const raw = localStorage.getItem(STORAGE_KEYS.userInfo);
    if (!raw)
        return null;
    try {
        return JSON.parse(raw);
    }
    catch (_a) {
        return null;
    }
}
/** 仅更新 userInfo 缓存（不碰 token）
 *  写 localStorage 后派发 auth:user-updated 事件，所有监听该事件的页面（header、account 等）
 *  可以在事件回调中重新 getUserInfo() 刷新自己。
 */
export function saveUserInfo(userInfo) {
    if (typeof window === 'undefined' || !userInfo)
        return;
    localStorage.setItem(STORAGE_KEYS.userInfo, JSON.stringify(userInfo));
    try {
        window.dispatchEvent(new CustomEvent('auth:user-updated', { detail: userInfo }));
    }
    catch (_b) { /* ignore */ }
}
/**
 * 保存登录响应数据
 * @param data { accessToken, refreshToken, expiresIn, userInfo }
 */
export function saveAuth(data) {
    if (typeof window === 'undefined' || !data)
        return;
    if (data.accessToken)
        localStorage.setItem(STORAGE_KEYS.accessToken, data.accessToken);
    if (data.refreshToken)
        localStorage.setItem(STORAGE_KEYS.refreshToken, data.refreshToken);
    if (typeof data.expiresIn === 'number') {
        const expiresAt = Date.now() + data.expiresIn * 1000;
        localStorage.setItem(STORAGE_KEYS.expiresAt, String(expiresAt));
    }
    if (data.userInfo)
        localStorage.setItem(STORAGE_KEYS.userInfo, JSON.stringify(data.userInfo));
}
/** 仅更新 accessToken / refreshToken / expiresAt（用于 refresh 接口） */
export function updateAccessToken(data) {
    if (typeof window === 'undefined' || !data)
        return;
    if (data.accessToken)
        localStorage.setItem(STORAGE_KEYS.accessToken, data.accessToken);
    if (data.refreshToken)
        localStorage.setItem(STORAGE_KEYS.refreshToken, data.refreshToken);
    if (typeof data.expiresIn === 'number') {
        const expiresAt = Date.now() + data.expiresIn * 1000;
        localStorage.setItem(STORAGE_KEYS.expiresAt, String(expiresAt));
    }
}
export function clearToken() {
    if (typeof window === 'undefined')
        return;
    localStorage.removeItem(STORAGE_KEYS.accessToken);
    localStorage.removeItem(STORAGE_KEYS.refreshToken);
    localStorage.removeItem(STORAGE_KEYS.expiresAt);
    localStorage.removeItem(STORAGE_KEYS.userInfo);
}
/** 触发全局未授权事件 */
function fireUnauthorized() {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('auth:unauthorized'));
    }
}
/* ========== 401 自动刷新（单例锁） ========== */
let refreshingPromise = null;
/**
 * 用 refreshToken 刷新 accessToken
 * 返回 { success, data, error }
 */
async function doRefresh() {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
        return { success: false, error: 'no_refresh_token' };
    }
    try {
        const res = await fetch(`/auth/refresh`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${refreshToken}`,
            },
            body: JSON.stringify({ refreshToken }),
        });
        const json = await res.json();
        const ok = json && (json.code === 200 || json.code === 0);
        if (!ok) {
            return { success: false, error: json?.message || 'refresh_failed' };
        }
        const data = json.data || {};
        updateAccessToken({
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            expiresIn: data.expiresIn,
        });
        return { success: true, data };
    }
    catch (e) {
        return { success: false, error: 'network_error' };
    }
}
/**
 * 获取一个新的 accessToken（处理并发：只有一个真实 refresh 请求）
 */
function refreshAccessToken() {
    if (refreshingPromise) {
        return refreshingPromise;
    }
    refreshingPromise = doRefresh().finally(() => {
        // 释放锁（无论成功失败）
        setTimeout(() => { refreshingPromise = null; }, 0);
    });
    return refreshingPromise;
}
/* ========== URL 参数序列化 ========== */
function buildQuery(params) {
    if (!params)
        return '';
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null || value === '')
            continue;
        search.append(key, String(value));
    }
    const qs = search.toString();
    return qs ? `?${qs}` : '';
}
/* ========== 解析响应 ========== */
function parseResponse(json) {
    // 无包装：直接返回原始数据
    if (json === null ||
        typeof json !== 'object' ||
        Array.isArray(json) ||
        !('code' in json)) {
        return { success: true, data: json };
    }
    const result = json;
    const ok = result.code === 200 || result.code === 0;
    if (!ok) {
        return { success: false, error: result.message || '请求失败' };
    }
    // 分页结构（records 数组）
    const data = result.data;
    if (data &&
        typeof data === 'object' &&
        !Array.isArray(data) &&
        'records' in data) {
        const page = data;
        const pagination = {
            page: page.current ?? page.pageNum,
            limit: page.size ?? page.pageSize,
            total: page.total,
            totalPages: page.pages ?? page.totalPages,
        };
        return { success: true, data: page.records, pagination };
    }
    return { success: true, data: data };
}
/* ========== 单次请求（不处理 401 重试） ========== */
async function rawRequest(method, url, config = {}) {
    const { params, body, timeout = DEFAULT_TIMEOUT, headers = {}, withToken = true, isFormData = false, } = config;
    const finalUrl = `${API_BASE_URL}${url}${buildQuery(params)}`;
    const requestHeaders = Object.assign({}, headers);
    if (!isFormData) {
        requestHeaders['Content-Type'] = 'application/json';
    }
    if (withToken) {
        const token = getToken();
        if (token)
            requestHeaders['Authorization'] = `Bearer ${token}`;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    try {
        const res = await fetch(finalUrl, {
            method,
            headers: requestHeaders,
            body: isFormData
                ? body
                : body !== undefined
                    ? JSON.stringify(body)
                    : undefined,
            signal: controller.signal,
            cache: 'no-store',
        });
        // 401 不在此处抛错，由外层 request() 处理
        if (res.status === 401) {
            return { __unauthorized: true, status: 401 };
        }
        if (!res.ok) {
            throw new ApiError(res.status, `请求失败（HTTP ${res.status}）`);
        }
        const json = await res.json();
        return { __json: parseResponse(json) };
    }
    finally {
        clearTimeout(timer);
    }
}
/* ========== 主请求函数（处理 401 自动刷新 + 重试） ========== */
async function request(method, url, config = {}) {
    console.log(`API Request: ${method} ${url}`, config);
    try {
        const first = await rawRequest(method, url, config);
        if (first.__unauthorized) {
            // 尝试 refresh
            const refreshResult = await refreshAccessToken();
            if (!refreshResult.success) {
                clearToken();
                fireUnauthorized();
                throw new ApiError(401, '登录已过期，请重新登录');
            }
            // 用新 token 重试一次
            const second = await rawRequest(method, url, config);
            if (second.__unauthorized) {
                // 仍然 401 → refreshToken 也过期
                clearToken();
                fireUnauthorized();
                throw new ApiError(401, '登录已过期，请重新登录');
            }
            return second.__json;
        }
        return first.__json;
    }
    catch (err) {
        if (err instanceof ApiError)
            throw err;
        if (err instanceof DOMException && err.name === 'AbortError') {
            throw new ApiError(408, '请求超时，请检查后端服务是否启动');
        }
        throw new ApiError(0, `无法连接后端服务（${API_BASE_URL}），请确认 Java 服务已启动`);
    }
}
/** 统一的 API 客户端实例 */
export const apiClient = {
    get: (url, config) => request('GET', url, config),
    post: (url, body, config) => request('POST', url, Object.assign(Object.assign({}, config), { body })),
    put: (url, body, config) => request('PUT', url, Object.assign(Object.assign({}, config), { body })),
    patch: (url, body, config) => request('PATCH', url, Object.assign(Object.assign({}, config), { body })),
    delete: (url, config) => request('DELETE', url, config),
};
export default apiClient;
